(function () {
  'use strict';

  var SHELL_SELECTOR = '[data-pdp-deferred-section-id]';
  var BATCH_SIZE = 5;
  var LOOKAHEAD_COUNT = 4;
  var LOAD_ROOT_MARGIN = '1200px 0px';

  var shells = [];
  var shellById = {};
  var pendingIds = [];
  var pendingIdMap = {};
  var loadingIdMap = {};

  /**
   * 判断当前浏览器是否支持性能壳依赖的能力。
   * 不支持时直接加载全部占位，避免下半页永远空白。
   */
  function supportsDeferredSections() {
    return 'IntersectionObserver' in window && 'fetch' in window && 'URL' in window;
  }

  /**
   * 返回当前页面 URL，并保留 variant / utm 等参数。
   * Section Rendering API 需要在当前 product URL 上追加 sections 参数，
   * 这样 Liquid 仍然拥有 product/template 的上下文。
   */
  function buildSectionsUrl(ids) {
    var url = new URL(window.location.href);
    url.searchParams.set('sections', ids.join(','));
    url.searchParams.set('pdp_deferred_sections', '1');
    return url.toString();
  }

  /**
   * 找到占位所在的 Shopify section wrapper。
   * Shopify JSON template 会自动包一层 #shopify-section-[section-id]，
   * Section Rendering API 返回的 HTML 也包含这层 wrapper，所以替换 wrapper 最干净。
   */
  function getSectionWrapper(shell) {
    return shell.closest('.shopify-section') || shell;
  }

  /**
   * 判断 script 是否应该重新执行。
   * 插入 innerHTML 得到的 script 不会自动执行；但 application/json 这类数据脚本不能执行。
   */
  function shouldExecuteScript(script) {
    var type = (script.getAttribute('type') || '').trim().toLowerCase();
    return !type || type === 'text/javascript' || type === 'application/javascript' || type === 'module';
  }

  /**
   * 复制 script 的所有属性和内容，替换原 script 以触发执行。
   * 外链脚本和内联脚本都走同一条路径，保持 section 原有初始化逻辑。
   */
  function executeScript(script) {
    if (!shouldExecuteScript(script)) return;

    var executable = document.createElement('script');
    Array.prototype.forEach.call(script.attributes, function (attr) {
      executable.setAttribute(attr.name, attr.value);
    });
    executable.text = script.text || script.textContent || '';
    script.parentNode.replaceChild(executable, script);
  }

  /**
   * 兼容异步插入 section 后错过的生命周期事件。
   * 很多旧 section 依赖 alpine:init / DOMContentLoaded 注册组件或初始化 tooltip；
   * 异步加载时这些事件已经发生，所以执行 section 脚本期间临时把它们补发给当前回调。
   */
  function withImmediateLifecycleEvents(callback) {
    var originalDocumentAddEventListener = document.addEventListener;
    var originalWindowAddEventListener = window.addEventListener;

    function callSoon(target, listener, eventName) {
      window.setTimeout(function () {
        listener.call(target, new Event(eventName));
      }, 0);
    }

    function shouldRunLoadedCallback(type) {
      return type === 'DOMContentLoaded' && document.readyState !== 'loading';
    }

    document.addEventListener = function (type, listener, options) {
      if (type === 'alpine:init' && window.Alpine && typeof listener === 'function') {
        listener.call(document, new Event('alpine:init'));
        return;
      }

      if (shouldRunLoadedCallback(type) && typeof listener === 'function') {
        callSoon(document, listener, type);
        return;
      }

      return originalDocumentAddEventListener.call(document, type, listener, options);
    };

    window.addEventListener = function (type, listener, options) {
      if (shouldRunLoadedCallback(type) && typeof listener === 'function') {
        callSoon(window, listener, type);
        return;
      }

      if (type === 'load' && document.readyState === 'complete' && typeof listener === 'function') {
        callSoon(window, listener, type);
        return;
      }

      return originalWindowAddEventListener.call(window, type, listener, options);
    };

    try {
      callback();
    } finally {
      document.addEventListener = originalDocumentAddEventListener;
      window.addEventListener = originalWindowAddEventListener;
    }
  }

  /**
   * 替换单个占位 section。
   * 返回的 HTML 为空时不替换，只把占位标记为 error，方便排查 Shopify 返回 null 的情况。
   */
  function replaceShell(sectionId, html) {
    var shell = shellById[sectionId];
    if (!shell || !html) {
      if (shell) shell.dataset.pdpDeferredState = 'error';
      return;
    }

    var wrapper = getSectionWrapper(shell);
    var template = document.createElement('template');
    template.innerHTML = html.trim();

    var nextWrapper = template.content.firstElementChild;
    if (!nextWrapper) {
      shell.dataset.pdpDeferredState = 'error';
      return;
    }

    if (nextWrapper.querySelector(SHELL_SELECTOR)) {
      shell.dataset.pdpDeferredState = 'error';
      shell.dataset.pdpDeferredError = 'placeholder-response';
      return;
    }

    wrapper.parentNode.replaceChild(nextWrapper, wrapper);

    withImmediateLifecycleEvents(function () {
      Array.prototype.forEach.call(nextWrapper.querySelectorAll('script'), executeScript);
    });

    if (window.Alpine && typeof window.Alpine.initTree === 'function') {
      window.Alpine.initTree(nextWrapper);
    }

    document.dispatchEvent(
      new CustomEvent('shopify:section:load', {
        detail: {
          sectionId: sectionId,
          target: nextWrapper,
        },
      })
    );

    document.dispatchEvent(
      new CustomEvent('satellai:pdp-deferred-section-loaded', {
        detail: {
          sectionId: sectionId,
          target: nextWrapper,
        },
      })
    );
  }

  /**
   * 按 Shopify 限制最多 5 个 section 一组拉取 HTML。
   * 请求失败时保留占位，并把状态标成 error，避免无休止重试抢主线程。
   */
  function loadBatch(ids) {
    ids.forEach(function (id) {
      loadingIdMap[id] = true;
      if (shellById[id]) shellById[id].dataset.pdpDeferredState = 'loading';
    });

    fetch(buildSectionsUrl(ids), {
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Section Rendering failed: ' + response.status);
        return response.json();
      })
      .then(function (sections) {
        ids.forEach(function (id) {
          replaceShell(id, sections && sections[id]);
        });
      })
      .catch(function () {
        ids.forEach(function (id) {
          if (shellById[id]) shellById[id].dataset.pdpDeferredState = 'error';
        });
      });
  }

  /**
   * 消费待加载队列。
   * 这里按 5 个一组切分，匹配 Shopify Section Rendering API 的 sections 参数上限。
   */
  function flushQueue() {
    if (!pendingIds.length) return;

    var batch = pendingIds.splice(0, BATCH_SIZE);
    batch.forEach(function (id) {
      delete pendingIdMap[id];
    });

    loadBatch(batch);

    if (pendingIds.length) {
      window.setTimeout(flushQueue, 80);
    }
  }

  /**
   * 将占位加入加载队列，并顺手预取后面几个 section。
   * 这样用户连续向下滚动时不会每个 section 都等一次网络往返。
   */
  function enqueueShell(shell) {
    var index = shells.indexOf(shell);
    var candidates = index >= 0 ? shells.slice(index, index + LOOKAHEAD_COUNT + 1) : [shell];

    candidates.forEach(function (candidate) {
      var id = candidate && candidate.dataset.pdpDeferredSectionId;
      if (!id || pendingIdMap[id] || loadingIdMap[id]) return;

      pendingIdMap[id] = true;
      pendingIds.push(id);
      candidate.dataset.pdpDeferredState = 'queued';
    });

    window.setTimeout(flushQueue, 0);
  }

  /**
   * 初始化所有占位。
   * 正常浏览器使用 IntersectionObserver；老浏览器直接加载全部，保证内容可达。
   */
  function initDeferredSections() {
    shells = Array.prototype.slice.call(document.querySelectorAll(SHELL_SELECTOR));
    if (!shells.length) return;

    shellById = shells.reduce(function (map, shell) {
      map[shell.dataset.pdpDeferredSectionId] = shell;
      return map;
    }, {});

    if (!supportsDeferredSections()) {
      shells.forEach(enqueueShell);
      return;
    }

    enqueueShell(shells[0]);

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          observer.unobserve(entry.target);
          enqueueShell(entry.target);
        });
      },
      {
        rootMargin: LOAD_ROOT_MARGIN,
      }
    );

    shells.forEach(function (shell) {
      shell.dataset.pdpDeferredState = 'idle';
      observer.observe(shell);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDeferredSections);
  } else {
    initDeferredSections();
  }
})();
